import { reqQueueCreator } from './reqQueue';

/**
 * The callback pair jsmediatags.read takes, with the payload left open so
 * the queue can also run the audio properties read, whose payload is not a
 * tag. jsmediatags' own CallbackType is this with TagType filled in.
 */
export interface ReadCallbacks<T> {
  onSuccess: (data: T) => void;
  onError?: (error: any) => void;
}

export const tagReqQueueCreator = function (
  setActive: React.MutableRefObject<(task: string) => void>,
  disableActive: React.MutableRefObject<(task: string) => void>,
  baseReqQ?: ReturnType<typeof reqQueueCreator>,
) {
  baseReqQ =
    baseReqQ ||
    reqQueueCreator(
      () => setActive.current('tagQueue'),
      () => disableActive.current('tagQueue'),
    );

  function callbackWrapper<T>(callbacks: ReadCallbacks<T>, resolve: Function, reject: Function) {
    return {
      onSuccess: (data: T) => {
        try {
          callbacks.onSuccess(data);
        } finally {
          resolve();
        }
      },
      onError: (error: any) => {
        try {
          if (callbacks.onError) {
            callbacks.onError(error);
          }
        } finally {
          reject(error);
        }
      },
    };
  }

  /** Receives and queues an id3 tag request from jsmediatags, or an audio properties read */
  return {
    queue<T>(
      readFn: (location: any, callbacks: ReadCallbacks<T>) => void,
      url: string,
      callbacks: ReadCallbacks<T>,
      /**
       * What the queue's watchdog calls this read if it has to give up on
       * it. Not the url: a Drive url names the file, and that stays out of
       * error reports.
       */
      label = 'tag read',
    ) {
      return baseReqQ!.queue(
        () =>
          new Promise(function (resolve, reject) {
            readFn(url, callbackWrapper(callbacks, resolve, reject));
          }),
        { label },
      );
    },
  };
};
